import React from 'react';
import { Pencil } from 'lucide-react';
import { safeQtyText } from './locationCases';

interface SafeQtyCellProps {
  quantity?: number | null;  // Prod Safe Qty; none shows —
  draft: string | null;  // null = not editing
  canEdit: boolean;
  saving: boolean;  // a save of this item is running: the pencil stays focusable but does nothing
  pencilRef: (el: HTMLButtonElement | null) => void;
  onEdit: () => void;
  onDraftChange: (draft: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  t: (key: string) => string;
}

const SafeQtyCell: React.FC<SafeQtyCellProps> = ({ quantity, draft, canEdit, saving, pencilRef, onEdit, onDraftChange, onCommit, onCancel, t }) => {
  const cs = t('itemSummaryReport.casesUnit');

  if (draft !== null) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={0}
          step={1}
          autoFocus
          aria-label={t('itemSummaryReport.editSafeQty')}
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={e => {
            // preventDefault: this Enter must not also press the dialog button that gets focus
            if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
            else if (e.key === 'Escape') onCancel();
          }}
          className="w-20 px-2 py-1 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <span className="text-gray-500 dark:text-gray-400">{cs}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {quantity != null
        ? <span className="font-semibold whitespace-nowrap">{safeQtyText(quantity, cs)}</span>
        : <span className="text-gray-400 dark:text-gray-500">—</span>}
      {canEdit && (
        <button
          ref={pencilRef}
          onClick={saving ? undefined : onEdit}
          aria-disabled={saving}
          title={t('itemSummaryReport.editSafeQty')}
          aria-label={t('itemSummaryReport.editSafeQty')}
          className={`p-0.5 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${saving ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-blue-600 dark:text-blue-400'}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default SafeQtyCell;
